// FÁZE 3 — Select: shortlist → Compare s Fit Score vůči focusu → Final Selection.
import React, { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Scales, Star, CheckCircle, ChartLineUp } from "@phosphor-icons/react";
import { useAuth } from "../../contexts/AuthContext";
import { getPool, listFocuses, selectCompare, focusSelect, FIT_LABELS, DECISION_TYPES } from "../../lib/lab-api";

const btn = "font-mono text-[10px] uppercase tracking-widest px-4 py-2";

function FinalSelectionForm({ focusId, rows, onDone }) {
    const [selected, setSelected] = useState(rows[0]?.fpl_id);
    const [decision, setDecision] = useState("buy_now");
    const [reason, setReason] = useState("");
    const [plannedGw, setPlannedGw] = useState("");
    const [busy, setBusy] = useState(false);

    const save = async () => {
        setBusy(true);
        try {
            const alts = rows.filter((r) => r.fpl_id !== Number(selected)).slice(0, 2).map((r) => r.fpl_id);
            await focusSelect(focusId, {
                selected_fpl_id: Number(selected), alternative_fpl_ids: alts,
                decision_type: decision, reason: reason || null,
                planned_gw: plannedGw ? Number(plannedGw) : null,
            });
            toast.success("Final Selection uložena — focus přesunut do stavu Selection Made");
            onDone();
        } catch (e) { toast.error(e.response?.data?.detail || "Uložení selhalo"); }
        finally { setBusy(false); }
    };

    return (
        <div className="border border-fa-cream/30 bg-fa-cream/5 p-4 space-y-3" data-testid="final-selection-form">
            <div className="font-mono text-[9px] uppercase tracking-widest text-fa-cream flex items-center gap-1.5">
                <CheckCircle size={12} /> Final Selection
            </div>
            <div className="grid md:grid-cols-4 gap-2">
                <label className="font-mono text-[8px] uppercase text-white/40 space-y-0.5 block">
                    <div>Hlavní kandidát</div>
                    <select value={selected} onChange={(e) => setSelected(e.target.value)} data-testid="final-selected"
                            className="w-full bg-black/40 border border-white/15 px-2 py-1.5 font-mono text-[10px]">
                        {rows.map((r) => <option key={r.fpl_id} value={r.fpl_id}>{r.name} (fit {Math.round(r.fit_score)})</option>)}
                    </select>
                </label>
                <label className="font-mono text-[8px] uppercase text-white/40 space-y-0.5 block">
                    <div>Rozhodnutí</div>
                    <select value={decision} onChange={(e) => setDecision(e.target.value)} data-testid="final-decision"
                            className="w-full bg-black/40 border border-white/15 px-2 py-1.5 font-mono text-[10px]">
                        {DECISION_TYPES.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
                    </select>
                </label>
                <label className="font-mono text-[8px] uppercase text-white/40 space-y-0.5 block">
                    <div>Plánovaný GW nákupu</div>
                    <input type="number" min={1} max={38} value={plannedGw} onChange={(e) => setPlannedGw(e.target.value)}
                           data-testid="final-planned-gw"
                           className="w-full bg-black/40 border border-white/15 px-2 py-1.5 font-mono text-[10px]" />
                </label>
                <label className="font-mono text-[8px] uppercase text-white/40 space-y-0.5 block">
                    <div>Důvod výběru</div>
                    <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="final-reason"
                           className="w-full bg-black/40 border border-white/15 px-2 py-1.5 font-mono text-[10px]" />
                </label>
            </div>
            <button type="button" onClick={save} disabled={busy} data-testid="final-save-btn"
                    className={`${btn} bg-fa-cream text-black font-bold disabled:opacity-40`}>
                {busy ? "Ukládám…" : "Potvrdit výběr"}
            </button>
        </div>
    );
}

export default function LabSelect() {
    const { user, login } = useAuth();
    const { refresh } = useOutletContext();
    const [items, setItems] = useState([]);
    const [focuses, setFocuses] = useState([]);
    const [picked, setPicked] = useState([]);
    const [focusId, setFocusId] = useState("");
    const [cmp, setCmp] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => {
        if (!user) return;
        getPool().then((d) => setItems((d.items || []).filter((e) => ["shortlist", "priority"].includes(e.status)))).catch(() => {});
        listFocuses().then((d) => { setFocuses(d.items || []); }).catch(() => {});
    }, [user]);
    useEffect(() => { load(); }, [load]);

    const toggle = (id) => setPicked((p) => p.includes(id) ? p.filter((x) => x !== id) : p.length < 5 ? [...p, id] : p);

    const compare = async () => {
        if (picked.length < 2) { toast.error("Vyber 2–5 hráčů"); return; }
        setBusy(true);
        try { setCmp(await selectCompare(picked, focusId || null)); }
        catch (e) { toast.error(e.response?.data?.detail || "Porovnání selhalo"); }
        finally { setBusy(false); }
    };

    if (!user) return (
        <div className="border border-white/10 bg-black/40 p-5 font-mono text-[11px] text-white/60" data-testid="select-login-cta">
            Pro shortlist se <button type="button" onClick={login} className="text-fa-mint underline">přihlas</button>.
        </div>
    );

    return (
        <div className="space-y-6" data-testid="lab-select">
            <p className="font-mono text-[10px] text-white/50 max-w-2xl">
                Vyber 2–5 kandidátů ze shortlistu, zvol Recruitment Focus a porovnej je <strong className="text-fa-mint">Fit Score</strong> —
                kdo nejlépe odpovídá konkrétnímu zadání, ne jen kdo je obecně nejlepší.
            </p>
            {items.length === 0 ? (
                <div className="border border-white/10 bg-black/40 p-5 font-mono text-[11px] text-white/50" data-testid="select-empty">
                    Shortlist je prázdný — přesuň hráče do Shortlist/Priority ve <Link to="/lab/scout" className="text-fa-mint underline">Scout</Link>.
                </div>
            ) : (
                <>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2" data-testid="select-shortlist">
                        {items.map((e) => (
                            <button key={e.id} type="button" onClick={() => toggle(e.fpl_id)}
                                    data-testid={`select-pick-${e.fpl_id}`}
                                    className={`text-left border p-3 transition-colors ${picked.includes(e.fpl_id) ? "border-fa-mint bg-fa-mint/10" : "border-white/10 bg-black/40 hover:border-white/30"}`}>
                                <div className="font-display font-bold text-sm flex items-center gap-1.5">
                                    {e.status === "priority" && <Star size={12} weight="fill" className="text-fa-mint" />}
                                    {e.name}
                                </div>
                                <div className="font-mono text-[9px] uppercase text-white/45 mt-0.5">
                                    {e.team} · {e.position}{e.price != null ? ` · £${e.price}` : ""}
                                </div>
                            </button>
                        ))}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <select value={focusId} onChange={(e) => setFocusId(e.target.value)} data-testid="select-focus"
                                className="bg-black/40 border border-white/15 px-2.5 py-2 font-mono text-[10px] uppercase">
                            <option value="">Bez focusu (obecné váhy)</option>
                            {focuses.map((f) => <option key={f.id} value={f.id}>{f.title} (GW{f.gw_start}–{f.gw_end})</option>)}
                        </select>
                        <button type="button" onClick={compare} disabled={busy || picked.length < 2} data-testid="select-compare-btn"
                                className={`${btn} bg-fa-mint text-black font-bold disabled:opacity-40 flex items-center gap-1.5`}>
                            <Scales size={12} /> {busy ? "Počítám…" : `Porovnat (${picked.length})`}
                        </button>
                    </div>
                </>
            )}

            {cmp && (
                <>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="compare-results">
                        {cmp.rows.map((r, idx) => (
                            <div key={r.fpl_id} className={`border p-4 space-y-2 ${idx === 0 ? "border-fa-mint/60 bg-fa-mint/5" : "border-white/10 bg-black/40"}`}
                                 data-testid={`compare-card-${r.fpl_id}`}>
                                <div className="flex items-start justify-between">
                                    <div>
                                        {idx === 0 && <div className="font-mono text-[8px] uppercase tracking-widest text-fa-mint">Nejlepší shoda</div>}
                                        <Link to={`/players/${r.fpl_id}`} data-testid={`compare-profile-${r.fpl_id}`}
                                              className="font-display font-black text-lg hover:text-fa-mint transition-colors">{r.name}</Link>
                                        <div className="font-mono text-[9px] uppercase text-white/45">{r.team} · {r.position} · £{r.price}</div>
                                    </div>
                                    <div className="text-right">
                                        <div className="font-display font-black text-2xl tabular-nums text-fa-mint">{Math.round(r.fit_score)}</div>
                                        <div className="font-mono text-[7px] uppercase text-white/35">Fit score</div>
                                    </div>
                                </div>
                                <div className="space-y-1">
                                    {Object.entries(r.fit_breakdown).map(([k, v]) => (
                                        <div key={k} className="flex items-center gap-2">
                                            <span className="font-mono text-[8px] uppercase text-white/40 w-14">{FIT_LABELS[k]}</span>
                                            <div className="flex-1 h-1.5 bg-white/5"><div className="h-full bg-fa-mint/70" style={{ width: `${v}%` }} /></div>
                                            <span className="font-mono text-[8px] tabular-nums text-white/50 w-6 text-right">{Math.round(v)}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="font-mono text-[9px] text-white/50">
                                    {r.total_points} pts 25/26 · {r.minutes} min · {r.ownership_pct}% own.
                                    {r.avg_fdr_window != null && ` · FDR okna ${r.avg_fdr_window}`}
                                </div>
                            </div>
                        ))}
                    </div>
                    {cmp.focus && <FinalSelectionForm focusId={cmp.focus.id} rows={cmp.rows} onDone={() => { setCmp(null); setPicked([]); load(); refresh(); }} />}
                    {!cmp.focus && (
                        <p className="font-mono text-[9px] text-white/40" data-testid="select-no-focus-hint">
                            Pro uložení Final Selection zvol při porovnání konkrétní Recruitment Focus.
                        </p>
                    )}
                </>
            )}

            <section className="space-y-2">
                <h2 className="font-display font-black uppercase text-base">Hloubkové nástroje</h2>
                <Link to="/tools/compare" data-testid="select-compare-tool-link"
                      className="border border-fa-sky/30 bg-fa-sky/5 p-4 flex items-start gap-3 hover:border-fa-sky/60 transition-colors group max-w-xl">
                    <ChartLineUp size={18} className="text-fa-sky flex-shrink-0 mt-0.5" />
                    <div>
                        <div className="font-display font-bold text-sm group-hover:text-fa-sky transition-colors">Historické porovnání</div>
                        <div className="font-mono text-[9px] text-white/50 mt-0.5">Porovnej 2–4 hráče přes bodové křivky a metriky napříč 6+ sezónami.</div>
                    </div>
                </Link>
            </section>
        </div>
    );
}
